// One loaded butterfly: model, mixer, four clips, perch placement and the
// idle variation that keeps a 10 s clip from reading as a loop. Flight
// movement is done by the state machine on `root`; the model itself never
// carries root motion (see docs/BUTTERFLY-ASSET-SPEC.md).
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { config } from '../config.js';

const CLIP_NAMES = ['fly', 'land', 'idle', 'takeoff'];
const UP = new THREE.Vector3(0, 1, 0);
const FORWARD = new THREE.Vector3(0, 0, 1); // the model faces +Z
const loader = new GLTFLoader();
const rand = (min, max) => min + Math.random() * (max - min);

export class Butterfly {
  static async load(spec, { log = () => {} } = {}) {
    const gltf = await loader.loadAsync(spec.model);
    return new Butterfly(gltf, spec, log);
  }

  constructor(gltf, spec, log) {
    this.spec = spec;
    this.log = log;
    this.root = new THREE.Group();
    this.root.name = `butterfly:${spec.id}`;
    this.model = gltf.scene;
    this.root.add(this.model);
    this.root.scale.setScalar((spec.wingspanM / spec.modelWingspanM) * config.insect.sizeMultiplier);
    this.root.visible = false;

    // Clips by exact name. A missing clip is a loud error, not a fallback.
    this.mixer = new THREE.AnimationMixer(this.model);
    this.actions = {};
    for (const name of CLIP_NAMES) {
      const clip = THREE.AnimationClip.findByName(gltf.animations, name);
      if (!clip) {
        log(`${spec.id}: clip "${name}" missing (have: ${gltf.animations.map((a) => a.name).join(', ')})`, 'error');
        continue;
      }
      const action = this.mixer.clipAction(clip);
      action.clampWhenFinished = true;
      this.actions[name] = action;
    }
    this.current = null;

    // Materials: replace the exported wing material with the project's own.
    this.wingMaterials = [];
    this.model.traverse((obj) => {
      if (!obj.isMesh) return;
      obj.frustumCulled = false; // skinned: bind-pose bounds are not the animated bounds
      if (obj.material?.name === 'wings') {
        obj.material = this.#wingMaterial(obj.material);
        this.wingMaterials.push(obj.material);
      } else if (obj.material) {
        obj.material.side = THREE.DoubleSide;
        if (spec.tint) obj.material.color.set(spec.tint).lerp(new THREE.Color(0xffffff), 0.5);
      }
    });

    // Forewing bone and its closed-pose rotation (idle starts closed), used
    // to decide when a random hold looks natural.
    this.wingBone = null;
    this.model.traverse((obj) => {
      if (!this.wingBone && obj.isBone && /l_frnt_wings01/.test(obj.name)) this.wingBone = obj;
    });
    this.wingClosedQuat = null;
    if (this.actions.idle && this.wingBone) {
      this.actions.idle.play();
      this.mixer.setTime(0);
      this.wingClosedQuat = this.wingBone.quaternion.clone();
      this.actions.idle.stop();
    } else if (!this.wingBone) {
      log(`${spec.id}: forewing bone not found; idle holds disabled`);
    }

    // Idle variation state
    this.hold = { until: 0, next: 0 };
    this.mixer.addEventListener('loop', (e) => {
      if (e.action === this.actions.idle) this.#rerollIdleSpeed();
    });
    this.time = 0;
  }

  #wingMaterial(source) {
    const m = new THREE.MeshStandardMaterial({
      map: source.map ?? null,
      normalMap: source.normalMap ?? null,
      roughness: 0.6,
      metalness: 0,
      side: THREE.DoubleSide,
      alphaTest: source.alphaMode === 'MASK' || source.alphaTest > 0 ? 0.5 : 0,
      // Cheap translucency: the albedo glows a little so a back-lit wing
      // never goes black. Phase 9 may replace this with real transmission.
      emissive: new THREE.Color(0xffffff),
      emissiveMap: source.map ?? null,
      emissiveIntensity: config.insect.wingGlow,
    });
    m.name = 'wings';
    if (this.spec.tint) {
      m.color.set(this.spec.tint);
      m.emissive.set(this.spec.tint);
    }
    return m;
  }

  /** Degrees the forewing has rotated away from the closed pose. */
  wingOpenness() {
    if (!this.wingBone || !this.wingClosedQuat) return 90;
    return THREE.MathUtils.radToDeg(this.wingBone.quaternion.angleTo(this.wingClosedQuat));
  }

  #rerollIdleSpeed() {
    const { speedMin, speedMax } = config.insect.idle;
    this.actions.idle.timeScale = rand(speedMin, speedMax) * config.insect.clipSpeed.idle;
  }

  /**
   * Play a clip, crossfading from the current one. Non-looping clips
   * (land, takeoff) clamp at their last frame; `onFinished` fires once.
   */
  play(name, { loop = name === 'fly' || name === 'idle', onFinished = null } = {}) {
    const action = this.actions[name];
    if (!action) return null;
    const fade = this.spec.crossfade ?? config.insect.crossfade;
    action.reset();
    action.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);
    action.timeScale = config.insect.clipSpeed[name] ?? 1;
    action.paused = false;
    action.enabled = true;
    if (this.current && this.current !== action) {
      action.crossFadeFrom(this.current, fade, true);
    }
    action.play();
    this.current = action;
    this.currentName = name;
    if (name === 'idle') {
      this.#rerollIdleSpeed();
      this.hold.next = this.time + rand(config.insect.idle.holdEveryMin, config.insect.idle.holdEveryMax);
      this.hold.until = 0;
    }
    if (!loop && onFinished) {
      const handler = (e) => {
        if (e.action !== action) return;
        this.mixer.removeEventListener('finished', handler);
        onFinished();
      };
      this.mixer.addEventListener('finished', handler);
    }
    return action;
  }

  /** Length of a clip in seconds at its configured speed. */
  duration(name) {
    const action = this.actions[name];
    if (!action) return 0;
    return action.getClip().duration / Math.abs(config.insect.clipSpeed[name] ?? 1);
  }

  /** Seconds remaining in a non-looping clip at its current speed. */
  remaining(name) {
    const action = this.actions[name];
    if (!action) return 0;
    return Math.max(0, (action.getClip().duration - action.time) / Math.abs(action.timeScale || 1));
  }

  /** Put the feet on a perch, oriented to its normal, facing `facing` (world direction) if given. */
  perchAt(perch, facing = null) {
    this.root.position.fromArray(perch.position);
    const normal = new THREE.Vector3().fromArray(perch.normal).normalize();
    this.root.quaternion.setFromUnitVectors(UP, normal);
    if (facing) {
      // Project the desired facing onto the perch plane and yaw to it.
      const f = facing.clone().projectOnPlane(normal);
      if (f.lengthSq() > 1e-8) {
        f.normalize();
        const currentForward = FORWARD.clone().applyQuaternion(this.root.quaternion);
        const angle = Math.atan2(currentForward.clone().cross(f).dot(normal), currentForward.dot(f));
        this.root.quaternion.premultiply(new THREE.Quaternion().setFromAxisAngle(normal, angle));
      }
    }
  }

  update(dt) {
    this.time += dt;
    // Idle holds: pause near the closed pose for a moment, then resume.
    if (this.currentName === 'idle' && this.actions.idle) {
      const idle = this.actions.idle;
      const c = config.insect.idle;
      if (idle.paused) {
        if (this.time >= this.hold.until) {
          idle.paused = false;
          this.hold.next = this.time + rand(c.holdEveryMin, c.holdEveryMax);
        }
      } else if (this.time >= this.hold.next && this.wingOpenness() <= c.holdMaxOpenness) {
        idle.paused = true;
        this.hold.until = this.time + rand(c.holdMin, c.holdMax);
      }
    }
    this.mixer.update(dt);
  }

  setVisible(v) {
    this.root.visible = v;
  }

  dispose() {
    this.mixer.stopAllAction();
    this.root.removeFromParent();
    this.model.traverse((obj) => {
      if (obj.isMesh) {
        obj.geometry?.dispose();
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        for (const m of mats) m?.dispose();
      }
    });
  }
}
